import { $, setLoading } from './dom.js';
import { api } from './api.js';
import { toastError, toastSuccess } from './toast.js';

/**
 * The list half of a resource module.
 *
 * Editing moved to its own page, so a list has one job left: republishing. The
 * three modules that publish something — role menus, ticket panels, embed
 * templates — asked for it identically, and three copies of six lines is three
 * chances for one of them to forget the spinner or swallow the error.
 *
 * Deletion is deliberately **not** here. It lives on the editor page, behind
 * the confirmation, where the person can see what they are about to destroy.
 * A delete button in a list is a delete button next to the wrong row.
 */
export interface ResourceListOptions {
  /** API segment (`reactions`, `tickets`, `embeds`). */
  endpoint: string;
  /** `data-*` prefix used by the cards (`menu`, `panel`, `template`). */
  prefix: string;
  guildId: string;
  /** Container holding the cards. */
  listSelector: string;
  successMessage: string;
}

export function mountResourceList(options: ResourceListOptions): void {
  const list = $(options.listSelector);
  if (!list) return;

  const attribute = `data-${options.prefix}-publish`;

  /*
   * A single listener on the container rather than one per card: the list is
   * rendered by the server, and binding every button would mean starting over
   * on each partial reload.
   */
  list.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>(`[${attribute}]`);
    if (!button) return;

    const id = button.getAttribute(attribute) ?? '';
    if (!id) return;

    void (async () => {
      setLoading(button, true);
      try {
        await api.post(`/api/${options.endpoint}/${id}/publish?guildId=${options.guildId}`, {});
        toastSuccess(options.successMessage);
      } catch (error) {
        toastError(error);
      } finally {
        setLoading(button, false);
      }
    })();
  });
}
